import React, { useCallback, useState } from 'react';
import { FileError } from 'react-dropzone';

import { allowedExtensions, allowedFileTypes, MAX_SIZE_OF_FILE } from '@/common/constants';
import REGEXPS from '@/common/regexp';
import { FileErrorCode } from '@/components/elements/DropScan/dropScan.types';

import { useCombineHSADocumentUploadQuery } from '../queries/useCombineHSADocumentUpload.query';
import { useCombineHSASubmitQuery } from '../queries/useCombineHSASubmit.query';
import { HSACombineAccount } from '../types/combineHSA.types';

const validateFile = (file: File): FileError | null => {
  const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
  const isValidType = allowedFileTypes.includes(file.type.toLowerCase() as typeof allowedFileTypes[number]) ||
    allowedExtensions.includes(ext as typeof allowedExtensions[number]);
  if (!isValidType) {
    return { code: FileErrorCode.FileInvalidType, message: '' };
  }
  if (file.size > MAX_SIZE_OF_FILE) {
    return { code: FileErrorCode.FileTooLarge, message: '' };
  }
  return null;
};

interface Params {
  employeeId: string;
  selectedFromAccount: HSACombineAccount;
  selectedToAccount: HSACombineAccount;
  onFileError: (error: FileError | null) => void;
  onSuccess: () => void;
}

export const useCombineHSAConfirm = ({ employeeId, selectedFromAccount, selectedToAccount, onFileError, onSuccess }: Params) => {
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentS3Key, setDocumentS3Key] = useState<string | null>(null);
  const [documentFileUrl, setDocumentFileUrl] = useState<string | null>(null);
  const [isDocPreviewModalVisible, setIsDocPreviewModalVisible] = useState(false);
  const [note, setNote] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [linkAccounts, setLinkAccounts] = useState(false);
  const [hasSubmitAttempted, setHasSubmitAttempted] = useState(false);
  const [isServerError, setIsServerError] = useState(false);

  const isDocumentError = hasSubmitAttempted && !documentFile;

  const { combineHSASubmit, isLoading: isSubmitting } = useCombineHSASubmitQuery();

  const { combineHSADocumentUpload, isLoading: isUploading } = useCombineHSADocumentUploadQuery();

  const onFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) return;
    const error = validateFile(file);
    if (error) {
      onFileError(error);
      return;
    }
    setDocumentFile(file);
    setDocumentS3Key(null);
    setDocumentFileUrl(null);
    try {
      const response = await combineHSADocumentUpload({ employeeId, file });
      setDocumentS3Key(response.data.s3key ?? null);
      setDocumentFileUrl(response.data.file_url ?? null);
    } catch {
      setDocumentFile(null);
      setDocumentFileUrl(null);
      onFileError({ code: FileErrorCode.InvalidFile, message: '' });
    }
  }, [employeeId, onFileError, combineHSADocumentUpload]);

  const onNoteChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.target.value;
      const withoutNewlines = value.replace(REGEXPS.NEWLINE, '');
      if (withoutNewlines.length > 2000) return;
      setNote(value);
    },
    []
  );

  const onSendEmailChange = useCallback((value: boolean) => setSendEmail(value), []);
  const onLinkAccountsChange = useCallback((value: boolean) => setLinkAccounts(value), []);
  const onDocPreviewOpen = useCallback(() => setIsDocPreviewModalVisible(true), []);
  const onDocPreviewClose = useCallback(() => setIsDocPreviewModalVisible(false), []);

  const onSubmit = useCallback(async () => {
    setHasSubmitAttempted(true);
    if (!documentFile || !documentS3Key) return;

    try {
      setIsServerError(false);
      await combineHSASubmit({
        employeeId: selectedFromAccount.employeeId,
        accountId: selectedFromAccount.accountId,
        payload: {
          account_id_to: Number(selectedToAccount.accountId),
          document_path: documentS3Key,
          note: note || undefined,
          send_email_notification_to_employee: sendEmail,
          link_accounts_after_transfer: linkAccounts,
        },
      });
      onSuccess();
    } catch {
      setIsServerError(true);
    }
  }, [documentFile, documentS3Key, selectedFromAccount, selectedToAccount, note, sendEmail, linkAccounts, combineHSASubmit, onSuccess]);

  return {
    documentFile,
    documentFileUrl,
    isDocPreviewModalVisible,
    onDocPreviewOpen,
    onDocPreviewClose,
    isUploading,
    isSubmitting,
    isServerError,
    setIsServerError,
    note,
    sendEmail,
    linkAccounts,
    isDocumentError,
    onFileSelect,
    onNoteChange,
    onSendEmailChange,
    onLinkAccountsChange,
    onSubmit,
  };
};
